import {
	differenceBy,
	first,
	flatten,
	flow,
	get,
	identity,
	isEmpty,
	last,
	partial,
	toPairs,
	uniq,
} from "lodash";
import {
	fetchChecksumsJSONFailed,
	fetchChecksumsJSONSuccess,
	unsubscribeFromLiveScores,
} from "modules/actions/checksums";
import {fetchDashboardSaga} from "modules/sagas/dashboard";
import fetchGameMessageSaga from "modules/sagas/gameMessage";
import fetchPlayersSaga from "modules/sagas/players";
import fetchRoundsSaga from "modules/sagas/rounds";
import fetchSquadsSaga from "modules/sagas/squads";
import {fetchLivePredictionsSaga, fetchPicksHistorySagaLive} from "modules/sagas/streak";
import fetchUnitsSaga, {fetchHistoryUnitsSaga} from "modules/sagas/units";
import {checksumsSelector} from "modules/selectors";
import {ISagaAction} from "modules/types";
import {Api} from "modules/utils/Api";
import {Saga, SagaIterator} from "redux-saga";
import {all, call, delay, put, race, select, take} from "typed-redux-saga/macro";
import {SagaGenerator} from "typed-redux-saga/types";
import {fetchLiveLeaderboard} from "modules/sagas/leaderboards";

const DELAY_SECONDS = 30;
const THOUSAND = 1000;

export enum LiveScoreSection {
	home = "home",
}

export enum CheckSumsKeys {
	players = "players",
	rounds = "rounds",
	squads = "squads",
	units = "units",
	game_message = "game_message",
	leaderboard = "leaderboard",
}

export interface ISubscribeToChecksums {
	section: LiveScoreSection;
}

type TMap = {
	[key in LiveScoreSection]: Saga[];
};

/**
 * Keys for listener
 */

type TMapChecksums = {
	[key in CheckSumsKeys]: TMap;
};

/**
 * If we need add some additional actions.
 * ladder - just a sections of APP
 *
 */
const mapChecksumsToSaga: TMapChecksums = {
	[CheckSumsKeys.players]: {
		[LiveScoreSection.home]: [fetchPlayersSaga],
	},
	[CheckSumsKeys.squads]: {
		[LiveScoreSection.home]: [fetchSquadsSaga],
	},
	[CheckSumsKeys.game_message]: {
		[LiveScoreSection.home]: [fetchGameMessageSaga],
	},
	[CheckSumsKeys.units]: {
		[LiveScoreSection.home]: [
			fetchUnitsSaga,
			fetchRoundsSaga,
			fetchDashboardSaga,
			fetchLivePredictionsSaga,
			fetchPicksHistorySagaLive,
			fetchHistoryUnitsSaga,
		],
	},
	[CheckSumsKeys.rounds]: {
		[LiveScoreSection.home]: [
			fetchRoundsSaga,
			fetchUnitsSaga,
			fetchDashboardSaga,
			fetchLivePredictionsSaga,
			fetchPicksHistorySagaLive,
			fetchHistoryUnitsSaga,
		],
	},
	[CheckSumsKeys.leaderboard]: {
		[LiveScoreSection.home]: [fetchLiveLeaderboard],
	},
};

export const fetchChecksumsJSONSaga = function* (): SagaGenerator<void> {
	try {
		const response = yield* call(Api.Jsons.checksums);

		yield* put(fetchChecksumsJSONSuccess(response.data.checksums));
	} catch (_e) {
		yield* put(fetchChecksumsJSONFailed());
	}
};

export const ChecksumsToSaga = function* (
	action: ISagaAction<ISubscribeToChecksums>
): SagaIterator {
	const {section} = action.payload;
	const checksums = yield* select(checksumsSelector);

	if (isEmpty(checksums)) {
		const {stopped_fetch} = yield* race({
			task: call(fetchChecksumsJSONSaga),
			stopped_fetch: take(unsubscribeFromLiveScores),
		} as Parameters<typeof race>[0]);

		if (stopped_fetch) {
			return;
		}
	}

	const TIME = THOUSAND * DELAY_SECONDS;

	const {stopped} = yield* race({
		wait: delay(TIME),
		stopped: take(unsubscribeFromLiveScores),
	} as Parameters<typeof race>[0]);

	if (!stopped) {
		const result = yield* all([
			select(checksumsSelector),
			call(fetchChecksumsJSONSaga),
		] as Parameters<typeof all>[0]);

		const old_checksums = result[0] as Record<string, string>;

		const new_checksums = yield* select(checksumsSelector);

		const requestsForChanges = uniq(
			flatten(
				differenceBy(toPairs(old_checksums), toPairs(new_checksums), (arr: string[]) => {
					return last<string>(arr ? arr : [""]);
				})
					.map(
						flow([
							first,
							partial(get, mapChecksumsToSaga),
							partial(get, partial.placeholder, section),
						])
					)
					.filter(identity)
			)
		);

		if (!isEmpty(requestsForChanges)) {
			yield* all(
				requestsForChanges.map((request) => {
					return call(request);
				}) as Parameters<typeof all>[0]
			);
		}

		yield* call(ChecksumsToSaga, action);
	}
};
